import React from "react";
import { Avatar, Box, Stack, Typography } from "@mui/material";

function formatRelativeTime(timestamp) {
  if (!timestamp) return "";
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMinutes = Math.max(0, Math.round((now - then) / 60000));
  if (diffMinutes < 60) return `${diffMinutes || 1}m ago`;
  if (diffMinutes < 24 * 60) return `${Math.round(diffMinutes / 60)}h ago`;
  const diffDays = Math.round(diffMinutes / 1440);
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(timestamp).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function MessageList({ messages = [], partner, onOpenPartner }) {
  if (!messages.length) {
    return (
      <Typography variant="body2" sx={{ color: "#718198" }}>
        No messages yet. Say something quick to start.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.1}>
      {messages.map((message) => {
        const isViewer = partner?.id
          ? String(message?.sender?.id || "") !== String(partner.id)
          : String(message?.sender?.username || "").toLowerCase() === "you";
        return (
          <Box
            key={String(message.id)}
            sx={{ display: "flex", justifyContent: isViewer ? "flex-end" : "flex-start" }}
          >
            <Stack
              direction={isViewer ? "row-reverse" : "row"}
              spacing={1}
              alignItems="flex-end"
              sx={{ maxWidth: "84%" }}
            >
              {!isViewer ? (
                <Box
                  role="button"
                  tabIndex={0}
                  aria-label={`Open ${message?.sender?.username || partner?.username || "person"} profile`}
                  onClick={onOpenPartner}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onOpenPartner?.();
                    }
                  }}
                  sx={{
                    borderRadius: "50%",
                    cursor: "pointer",
                    "&:focus-visible": {
                      outline: "2px solid rgba(23, 52, 93, 0.18)",
                      outlineOffset: 3,
                    },
                  }}
                >
                  <Avatar src={message?.sender?.profile_image || partner?.avatar || ""} sx={{ width: 30, height: 30 }}>
                    {(message?.sender?.username || partner?.username || "T").slice(0, 1).toUpperCase()}
                  </Avatar>
                </Box>
              ) : null}

              <Box
                sx={{
                  px: 1.6,
                  py: 1.25,
                  borderRadius: "18px",
                  borderBottomRightRadius: isViewer ? "8px" : "18px",
                  borderBottomLeftRadius: isViewer ? "18px" : "8px",
                  bgcolor: isViewer ? "#243f70" : "#f7f9fc",
                  color: isViewer ? "#f8fbff" : "#102033",
                  border: isViewer ? "none" : "1px solid #dde6f0",
                }}
              >
                <Typography sx={{ lineHeight: 1.55, color: isViewer ? "#f8fbff" : "#102033", fontWeight: 500 }}>
                  {message?.body || ""}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.6,
                    color: isViewer ? "rgba(248,251,255,0.72)" : "#70839a",
                    fontWeight: 600,
                  }}
                >
                  {isViewer ? (
                    "You"
                  ) : (
                    <Box
                      component="span"
                      role="button"
                      tabIndex={0}
                      onClick={onOpenPartner}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onOpenPartner?.();
                        }
                      }}
                      sx={{
                        cursor: "pointer",
                        fontWeight: 800,
                        "&:hover": { textDecoration: "underline" },
                        "&:focus-visible": {
                          outline: "2px solid rgba(23, 52, 93, 0.18)",
                          outlineOffset: 2,
                        },
                      }}
                    >
                      {message?.sender?.username || partner?.username}
                    </Box>
                  )}{" "}
                  · {formatRelativeTime(message?.created_at)}
                </Typography>
              </Box>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}
